// src/components/Atelier4/ScenarioRiskCalculation.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Edit3, Save, X, BarChart3 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  calculateVraisemblance,
  getVraisemblanceLabel,
  getVraisemblanceColor,
  getPSLabel,
  getDTLabel,
  getFullMatrix
} from '../../utils/vraisemblanceCalculator';

// Helper functions defined outside component to avoid hoisting issues
const getDefaultRiskData = (method) => {
  switch (method) {
    case 'express':
      return {
        globalVraisemblance: 3,
        estimationMode: 'direct'
      };
    case 'standard':
      return {
        stepProbabilities: [3, 3, 3, 3],
        defenseLevel: 'intermediate'
      };
    case 'advanced':
      return {
        stepProbabilities: [3, 3, 3, 3],
        stepDifficulties: [2, 2, 2, 2],
        defenseLevel: 'intermediate'
      };
    default:
      return {};
  }
};

const detectRiskMethod = (scenario) => {
  // 1. Check for AI Modes (Priority)
  if (scenario.modesOperatoires && scenario.modesOperatoires.length > 0) {
    // If we have elementary actions with PS/DT, we can use Advanced
    const hasPSDT = scenario.elementaryActions?.some(a =>
      (a.successProbability || a.ps) && (a.technicalDifficulty || a.dt)
    );
    return hasPSDT ? 'advanced' : 'standard';
  }

  // 2. Fallback to Operational Steps (Legacy/Manual)
  if (!scenario.operationalSteps) return 'express';

  const hasFullData = scenario.operationalSteps.every(step =>
    step.stepProbability && step.difficulty
  );

  if (hasFullData) return 'advanced';

  const hasProbabilities = scenario.operationalSteps.some(step => step.stepProbability);
  if (hasProbabilities) return 'standard';

  return 'express';
};

const extractRiskDataFromScenario = (scenario, method) => {
  // Handle Modes-based data
  if (scenario.modesOperatoires && (method === 'standard' || method === 'advanced')) {
    return {
      modes: scenario.modesOperatoires,
      elementaryActions: scenario.elementaryActions || [],
      defenseLevel: 'intermediate' // Default
    };
  }

  // Handle Legacy/Manual data
  if (!scenario.operationalSteps) return getDefaultRiskData(method);

  switch (method) {
    case 'advanced':
      return {
        stepProbabilities: scenario.operationalSteps.map(step => step.stepProbability || 3),
        stepDifficulties: scenario.operationalSteps.map(step => step.difficulty || 2),
        defenseLevel: 'intermediate'
      };
    case 'standard':
      return {
        stepProbabilities: scenario.operationalSteps.map(step => step.stepProbability || 3),
        defenseLevel: 'intermediate'
      };
    case 'express':
    default:
      return {
        globalVraisemblance: scenario.scenarioVraisemblance || 3,
        estimationMode: 'direct'
      };
  }
};

// Note: calculateVraisemblance, getVraisemblanceLabel, getVraisemblanceColor 
// are now imported from vraisemblanceCalculator utility

// Helper to get vraisemblance color classes for Tailwind
const getVraisemblanceColorClasses = (value) => {
  const colors = {
    1: 'text-blue-600 bg-blue-100',
    2: 'text-yellow-600 bg-yellow-100',
    3: 'text-orange-600 bg-orange-100',
    4: 'text-red-600 bg-red-100'
  };
  return colors[value] || 'text-gray-600 bg-gray-100';
};


const calculateRiskMetrics = (scenario, method, data) => {
  let vraisemblance = 3;
  let probability = 'high';  // Use key instead of French label

  // 1. Get modes from data or scenario
  const modes = data.modes?.length > 0 ? data.modes : scenario?.modesOperatoires || [];
  const elementaryActions = data.elementaryActions?.length > 0 ? data.elementaryActions : scenario?.elementaryActions || [];

  // 2. Modes-based Calculation (EBIOS RM 2018)
  if (modes.length > 0 && (method === 'standard' || method === 'advanced')) {
    // Only retained modes contribute to the scenario vraisemblance
    const retainedModes = modes.filter((m) => m && m.retained !== false);
    if (retainedModes.length === 0) {
      // No mode retained → scenario V is "not evaluated" (caller reads it as null)
      vraisemblance = null;
    } else {
      const modeVs = retainedModes.map(mode => {
        // Get actions for this mode
        const actionIds = mode.path || [];
        const actions = elementaryActions.filter(a => actionIds.includes(a.elementaryActionId));

        // If mode has pre-calculated vraisemblance and no detailed actions, use it
        if (actions.length === 0 && mode.vraisemblance) {
          return mode.vraisemblance;
        }

        if (actions.length === 0) return 2; // Default

        // Extract PS values from actions
        const psValues = actions
          .map(a => a.successProbability || a.ps || 2)
          .filter(v => v >= 1 && v <= 4);

        if (psValues.length === 0) return mode.vraisemblance || 2;

        const minPS = Math.min(...psValues);

        if (method === 'advanced') {
          // ADVANCED: V(Mode) = MATRIX[MAX(DT)][MIN(PS)]
          const dtValues = actions
            .map(a => a.technicalDifficulty || a.dt || 2)
            .filter(v => v >= 1 && v <= 4);

          const maxDT = dtValues.length > 0 ? Math.max(...dtValues) : 2;
          return calculateVraisemblance(minPS, maxDT);
        } else {
          // STANDARD: V(Mode) = MIN(PS) - direct, no matrix
          return minPS;
        }
      });

      // Scenario V = MAX of Mode Vs (for both methods)
      vraisemblance = modeVs.length > 0 ? Math.max(...modeVs) : 3;
    }
  }
  // 3. Express method
  else if (method === 'express') {
    if (data.estimationMode === 'direct') {
      vraisemblance = data.globalVraisemblance || scenario?.scenarioVraisemblance || 3;
    } else {
      vraisemblance = calculateVraisemblance(
        data.scenarioProbability || 3,
        data.scenarioDifficulty || 2
      );
    }
  }
  // 4. Legacy Calculation (Operational Steps - for backwards compatibility only)
  else if (method === 'standard' && data.stepProbabilities) {
    // Legacy standard: should not be used anymore
    vraisemblance = Math.min(...data.stepProbabilities.filter(v => v >= 1 && v <= 4));
  } else if (method === 'advanced' && data.stepProbabilities && data.stepDifficulties) {
    // Legacy advanced: should not be used anymore
    const minPS = Math.min(...data.stepProbabilities.filter(v => v >= 1 && v <= 4));
    const maxDT = Math.max(...data.stepDifficulties.filter(v => v >= 1 && v <= 4));
    vraisemblance = calculateVraisemblance(minPS, maxDT);
  }
  // 5. Fallback: use scenario's stored vraisemblance
  else if (scenario?.scenarioVraisemblance) {
    vraisemblance = scenario.scenarioVraisemblance;
  }


  // Calculate derived metrics (return keys, not translated labels)
  probability = getProbabilityKey(vraisemblance);

  // Impact and Final Risk are removed as per user request (focus on Likelihood only)

  return { vraisemblance, probability };
};

const getDefenseAdjustment = (level) => {
  const adjustments = {
    'low': 0,
    'intermediate': 0.5,
    'high': 1,
    'very_high': 1.5
  };
  return adjustments[level] || 0.5;
};

// Returns key for translation (e.g., 'veryLow', 'low', 'high', 'veryHigh')
const getProbabilityKey = (vraisemblance) => {
  const keys = {
    1: 'veryLow',
    2: 'low',
    3: 'high',
    4: 'veryHigh'
  };
  return keys[vraisemblance] || 'medium';
};


const getCalculationDetails = (method, data, calculatedValues, scenario = null, t) => {
  // Get modes from data or scenario
  const modes = data.modes?.length > 0 ? data.modes : scenario?.modesOperatoires || [];
  const modesCount = modes.length;

  // Handle Modes-based calculation details
  if (modesCount > 0 && (method === 'standard' || method === 'advanced')) {
    if (method === 'advanced') {
      return `Avancé: V(Mode) = MATRIX[MAX(DT)][MIN(PS)] → V(Scénario) = MAX(${modesCount} modes) = ${calculatedValues.vraisemblance}/4`;
    } else {
      return `Standard: V(Mode) = MIN(PS) → V(Scénario) = MAX(${modesCount} modes) = ${calculatedValues.vraisemblance}/4`;
    }
  }

  switch (method) {
    case 'express':
      if (data.estimationMode === 'direct') {
        return t('workshop4.risk.assessment.likelihoodDirect', { value: `${calculatedValues.vraisemblance}/4` });
      } else {
        return `Calculé: MATRIX[DT=${data.scenarioDifficulty || 2}][PS=${data.scenarioProbability || 3}] = ${calculatedValues.vraisemblance}/4`;
      }
    case 'standard':
      return `Standard: V(Scénario) = MAX(V modes) = ${calculatedValues.vraisemblance}/4`;
    case 'advanced':
      return `Avancé: V(Scénario) = MAX(V modes) = ${calculatedValues.vraisemblance}/4`;
    default:
      return 'Calcul automatique basé sur les paramètres saisis';
  }
};

const calculateScenarioRisk = (scenario, method, data) => {
  const calculatedValues = calculateRiskMetrics(scenario, method, data);

  const updatedScenario = {
    ...scenario,
    scenarioVraisemblance: calculatedValues.vraisemblance,
    likelihood: calculatedValues.probability,
    impact: calculatedValues.impact,
    riskLevel: calculatedValues.finalRisk,
    riskCalculationMethod: {
      method,
      data
    }
  };

  // Update operational steps if they exist
  if (scenario.operationalSteps && (method === 'standard' || method === 'advanced')) {
    updatedScenario.operationalSteps = scenario.operationalSteps.map((step, index) => {
      let updatedStep = { ...step };

      if (method === 'advanced' && data.stepProbabilities && data.stepDifficulties) {
        updatedStep.stepProbability = data.stepProbabilities[index] || step.stepProbability || 3;
        updatedStep.difficulty = data.stepDifficulties[index] || step.difficulty || 2;
        updatedStep.vraisemblance = calculateVraisemblance(
          updatedStep.stepProbability,
          updatedStep.difficulty
        );
      } else if (method === 'standard' && data.stepProbabilities) {
        updatedStep.stepProbability = data.stepProbabilities[index] || step.stepProbability || 3;
        const defenseLevel = data.defenseLevel || 'intermediate';
        updatedStep.difficulty = getDefenseAdjustment(defenseLevel) + 1;
        updatedStep.vraisemblance = Math.max(1, Math.min(4,
          Math.round(updatedStep.stepProbability - getDefenseAdjustment(defenseLevel))
        ));
      }

      return updatedStep;
    });
  }

  return updatedScenario;
};

const ScenarioRiskCalculation = ({
  scenario,
  onScenarioUpdate,
  onVraisemblancePreview,
  isEditable = true,
  showEditButton = true,
  className = ""
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [riskMethod, setRiskMethod] = useState(scenario?.riskCalculationMethod?.method || 'express');
  const [riskData, setRiskData] = useState(scenario?.riskCalculationMethod?.data || getDefaultRiskData('express'));
  const [showMethodInfo, setShowMethodInfo] = useState(false);
  const [isCompact, setIsCompact] = useState(true);

  // Initialize risk data if scenario doesn't have it
  useEffect(() => {
    if (scenario && !scenario.riskCalculationMethod) {
      // Auto-detect method based on existing scenario data
      const detectedMethod = detectRiskMethod(scenario);
      setRiskMethod(detectedMethod);
      setRiskData(extractRiskDataFromScenario(scenario, detectedMethod));
    }
  }, [scenario]);

  // Reset local state when scenario changes (e.g., when switching scenarios)
  useEffect(() => {
    if (scenario?.riskCalculationMethod) {
      setRiskMethod(scenario.riskCalculationMethod.method || 'express');
      setRiskData(scenario.riskCalculationMethod.data || getDefaultRiskData('express'));
    }
  }, [scenario?.id]);

  // Emit live vraisemblance preview to parent (badge sync)
  const previewValues = calculateRiskMetrics(scenario, riskMethod, riskData);
  useEffect(() => {
    if (onVraisemblancePreview && scenario?.attackPathId) {
      onVraisemblancePreview(scenario.attackPathId, previewValues.vraisemblance);
    }
  }, [previewValues.vraisemblance, scenario?.attackPathId]);

  const handleSave = async () => {
    try {
      console.log('🔄 [ScenarioRiskCalculation] Saving risk parameters...', {
        method: riskMethod,
        data: riskData,
        scenarioId: scenario?.id
      });

      const updatedScenario = {
        ...scenario,
        riskCalculationMethod: {
          method: riskMethod,
          data: riskData
        }
      };

      // Handle elementary actions if present in riskData
      if (riskData.elementaryActions && riskData.elementaryActions.length > 0) {
        updatedScenario.elementaryActions = riskData.elementaryActions;

        // Recalculate modes vraisemblance based on updated actions
        if (updatedScenario.modesOperatoires && updatedScenario.modesOperatoires.length > 0) {
          updatedScenario.modesOperatoires = updatedScenario.modesOperatoires.map(mode => {
            const modeActionIds = mode.path || [];
            const modeActions = modeActionIds
              .map(id => riskData.elementaryActions.find(a => a.elementaryActionId === id))
              .filter(Boolean);

            if (modeActions.length === 0) return mode;

            const psValues = modeActions.map(a => a.successProbability || a.ps || 2).filter(v => v >= 1 && v <= 4);
            const dtValues = modeActions.map(a => a.technicalDifficulty || a.dt || 2).filter(v => v >= 1 && v <= 4);

            if (psValues.length === 0) return mode;

            const minPS = Math.min(...psValues);
            const maxDT = dtValues.length > 0 ? Math.max(...dtValues) : 2;

            // Use matrix for advanced, MIN(PS) for standard
            const modeV = riskMethod === 'advanced'
              ? calculateVraisemblance(minPS, maxDT)
              : minPS;

            return { ...mode, vraisemblance: modeV };
          });

          // Calculate scenario V as MAX of retained mode Vs (null if none retained)
          const retainedModeVs = updatedScenario.modesOperatoires
            .filter((m) => m && m.retained !== false)
            .map((m) => m.vraisemblance)
            .filter((v) => v >= 1 && v <= 4);
          updatedScenario.scenarioVraisemblance = retainedModeVs.length > 0
            ? Math.max(...retainedModeVs)
            : null;
        }
      }
      // Legacy: Update scenario steps based on risk method
      else if (scenario.operationalSteps) {
        updatedScenario.operationalSteps = scenario.operationalSteps.map((step, index) => {
          let updatedStep = { ...step };

          if (riskMethod === 'advanced' && riskData.stepProbabilities && riskData.stepDifficulties) {
            updatedStep.stepProbability = riskData.stepProbabilities[index] || step.stepProbability;
            updatedStep.difficulty = riskData.stepDifficulties[index] || step.difficulty;
            updatedStep.vraisemblance = calculateVraisemblance(
              updatedStep.stepProbability,
              updatedStep.difficulty
            );
          } else if (riskMethod === 'standard' && riskData.stepProbabilities) {
            updatedStep.stepProbability = riskData.stepProbabilities[index] || step.stepProbability;
            const defenseLevel = riskData.defenseLevel || 'intermediate';
            if (!updatedStep.difficulty) {
              updatedStep.difficulty = defenseLevel === 'low' ? 2 :
                defenseLevel === 'high' ? 3 :
                  defenseLevel === 'very_high' ? 4 : 2;
            }
            updatedStep.vraisemblance = calculateVraisemblance(
              updatedStep.stepProbability,
              updatedStep.difficulty
            );
          }

          return updatedStep;
        });

        // Calculate overall scenario vraisemblance for legacy
        if (riskMethod === 'express' && riskData.estimationMode === 'direct') {
          updatedScenario.scenarioVraisemblance = riskData.globalVraisemblance;
        } else if (!riskData.elementaryActions) {
          const totalVraisemblance = updatedScenario.operationalSteps.reduce(
            (sum, step) => sum + (step.vraisemblance || 2), 0
          );
          updatedScenario.scenarioVraisemblance = Math.round(
            totalVraisemblance / updatedScenario.operationalSteps.length
          );
        }
      }

      // Calculate final metrics using the proper EBIOS RM calculation
      const calculatedValues = calculateRiskMetrics(updatedScenario, riskMethod, riskData);
      updatedScenario.scenarioVraisemblance = calculatedValues.vraisemblance;
      updatedScenario.likelihood = calculatedValues.probability;
      updatedScenario.impact = calculatedValues.impact;
      updatedScenario.riskLevel = calculatedValues.finalRisk;

      console.log('✅ [ScenarioRiskCalculation] Updated scenario:', {
        vraisemblance: updatedScenario.scenarioVraisemblance,
        method: updatedScenario.riskCalculationMethod.method,
        hasElementaryActions: !!(riskData.elementaryActions && riskData.elementaryActions.length > 0)
      });

      // Call the parent update function
      await onScenarioUpdate(updatedScenario);

      // Exit editing mode
      setIsEditing(false);

      console.log('💾 [ScenarioRiskCalculation] Risk parameters saved successfully');
    } catch (error) {
      console.error('❌ [ScenarioRiskCalculation] Error saving risk parameters:', error);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setRiskMethod(scenario?.riskCalculationMethod?.method || 'express');
    setRiskData(scenario?.riskCalculationMethod?.data || getDefaultRiskData('express'));
    setIsEditing(false);
  };

  const renderRiskDisplay = () => {
    const calculatedValues = calculateRiskMetrics(scenario, riskMethod, riskData);

    // Vraisemblance style and label
    const getVraisemblanceStyle = (level) => {
      const styles = {
        1: { bg: 'bg-emerald-500', text: 'text-emerald-500', icon: '🟢', label: t('common.likelihood.low', 'Peu vraisemblable') },
        2: { bg: 'bg-yellow-500', text: 'text-yellow-500', icon: '🟡', label: t('common.likelihood.medium', 'Vraisemblable') },
        3: { bg: 'bg-orange-500', text: 'text-orange-500', icon: '🟠', label: t('common.likelihood.high', 'Très vraisemblable') },
        4: { bg: 'bg-red-500', text: 'text-red-500', icon: '🔴', label: t('common.likelihood.critical', 'Quasi certain') }
      };
      return styles[level] || styles[2];
    };

    const riskStyle = getVraisemblanceStyle(calculatedValues.vraisemblance);

    return (
      <div className="relative">
        {/* Main Risk Dashboard */}
        <div className="bg-gradient-to-r from-slate-50 via-white to-slate-50 rounded-xl border border-slate-200 p-4 shadow-sm">

          {/* Risk Level Indicator */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 ${riskStyle.bg} rounded-full animate-pulse`}></div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">{t('workshop4.risk.level')}</div>
                <div className={`text-lg font-bold ${riskStyle.text}`}>
                  {riskStyle.label} ({calculatedValues.vraisemblance}/4)
                </div>
              </div>
            </div>

            {/* Method Badge + Edit Button */}
            <div className="flex items-center space-x-2">
              {renderMethodBadge()}
              {showEditButton && isEditable && (
                <motion.button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title={t('workshop4.risk.assessment.adjust')}
                >
                  <Edit3 className="w-4 h-4" />
                </motion.button>
              )}
            </div>
          </div>

          {/* Risk Metrics Bar */}
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="text-center">
              <div className="text-xs text-slate-500 mb-1">{t('workshop4.risk.likelihood')}</div>
              <div className="flex justify-center mb-1">
                {[1, 2, 3, 4].map((level) => (
                  <div
                    key={level}
                    className={`w-2 h-2 mx-0.5 rounded-full ${level <= calculatedValues.vraisemblance
                      ? getVraisemblanceStyle(level).bg
                      : 'bg-slate-200'
                      }`}
                  />
                ))}
              </div>
              <div className="text-xs font-medium text-slate-700">{calculatedValues.vraisemblance}/4</div>
            </div>

            <div className="text-center">
              <div className="text-xs text-slate-500 mb-1">{t('riskAssessment.successProbability')}</div>
              <div className="text-sm font-semibold text-slate-700">{t(`riskAssessment.probabilityLevels.${calculatedValues.probability}`)}</div>
            </div>
          </div>

          {/* Method-Specific Details */}
          {riskMethod === 'express' && (
            <div className="border-t border-slate-200 pt-3">
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">
                {t('workshop4.risk.quickAssessment', 'Évaluation rapide')}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white rounded-lg p-2 border border-slate-200 text-center">
                  <div className="text-xs text-slate-500">{t('workshop4.risk.mode', 'Mode')}</div>
                  <div className="text-sm font-medium text-slate-700">
                    {riskData.estimationMode === 'direct'
                      ? t('workshop4.risk.directEstimation', 'Direct')
                      : t('workshop4.risk.calculated', 'Calculé')}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-2 border border-slate-200 text-center">
                  <div className="text-xs text-slate-500">{t('workshop4.risk.result', 'Résultat')}</div>
                  <div className={`text-sm font-bold ${riskStyle.text}`}>{calculatedValues.vraisemblance}/4</div>
                </div>
              </div>
            </div>
          )}

          {/* Modes Opératoires Summary for Standard/Advanced */}
          {(riskMethod === 'standard' || riskMethod === 'advanced') && scenario.modesOperatoires && scenario.modesOperatoires.length > 0 && (
            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-slate-500 uppercase tracking-wide">
                  {t('workshop4.modesOperatoires.title', 'Modes Opératoires')}
                </div>
                <div className="text-xs text-green-600 flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></div>
                  {t('riskAssessment.synchronizedWithModes', 'Synced with modes')}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {scenario.modesOperatoires.slice(0, 4).map((mode, index) => {
                  const modeStyle = getVraisemblanceStyle(mode.vraisemblance || 2);
                  return (
                    <div key={index} className="bg-white rounded-lg p-2 border border-slate-200">
                      <div className="text-xs text-slate-600 font-medium mb-1 text-center">
                        Mode {index + 1}
                      </div>
                      <div className="flex items-center justify-center mb-1">
                        <div className={`w-full h-2 ${modeStyle.bg} rounded-full`}></div>
                      </div>
                      <div className="text-xs text-slate-700 font-bold text-center">
                        V: {mode.vraisemblance || 2}
                      </div>
                    </div>
                  );
                })}
              </div>
              {scenario.modesOperatoires.length > 4 && (
                <div className="text-xs text-slate-500 text-center mt-2">
                  +{scenario.modesOperatoires.length - 4} {t('workshop4.modesOperatoires.more', 'autres modes')}
                </div>
              )}
            </div>
          )}

          {/* Calculation Formula */}
          <div className="mt-3 pt-3 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500">
                {getCalculationDetails(riskMethod, riskData, calculatedValues, scenario, t)}
              </div>
              <button
                onClick={() => setIsCompact(!isCompact)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-50"
              >
                {isCompact ? `📊 ${t('riskAssessment.details')}` : `📋 ${t('riskAssessment.summary')}`}
              </button>
            </div>
          </div>
        </div>

        {/* Extended Details (when not compact) */}
        {!isCompact && (
          <div className="mt-3 bg-white rounded-lg border border-slate-200 p-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="bg-slate-50 rounded-lg p-2 text-center">
                <div className="text-slate-500 mb-1">{t('riskAssessment.estimatedDuration')}</div>
                <div className="font-semibold text-slate-700">{scenario?.timeline || '1-3h'}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2 text-center">
                <div className="text-slate-500 mb-1">{t('riskAssessment.skills')}</div>
                <div className="font-semibold text-slate-700">{scenario?.requiredSkills || t('riskAssessment.defenseLevels.intermediate')}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2 text-center">
                <div className="text-slate-500 mb-1">{t('riskAssessment.detection')}</div>
                <div className="font-semibold text-slate-700">
                  {calculatedValues.vraisemblance >= 3 ? t('riskAssessment.difficult') : t('riskAssessment.possible')}
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2 text-center">
                <div className="text-slate-500 mb-1">{t('riskAssessment.mitigation')}</div>
                <div className="font-semibold text-slate-700">
                  {calculatedValues.vraisemblance >= 3 ? t('riskAssessment.priority') : t('riskAssessment.standard')}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMethodBadge = () => {
    const methodLabels = {
      express: { label: t('workshop4.risk.methods.express.label'), icon: '⚡' },
      standard: { label: t('workshop4.risk.methods.standard.label'), icon: '📊' },
      advanced: { label: t('workshop4.risk.methods.advanced.label'), icon: '🎯' }
    };

    const method = methodLabels[riskMethod] || methodLabels.express;

    return (
      <div className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-600 rounded-md text-xs">
        <span className="mr-1">{method.icon}</span>
        {method.label}
      </div>
    );
  };

  const renderEditForm = () => {
    const previewValues = calculateRiskMetrics(scenario, riskMethod, riskData);
    const getVraisemblanceStyle = (level) => {
      const styles = {
        1: { bg: 'bg-emerald-500', text: 'text-emerald-600', label: t('common.likelihood.low', 'Peu vraisemblable') },
        2: { bg: 'bg-yellow-500', text: 'text-yellow-600', label: t('common.likelihood.medium', 'Vraisemblable') },
        3: { bg: 'bg-orange-500', text: 'text-orange-600', label: t('common.likelihood.high', 'Très vraisemblable') },
        4: { bg: 'bg-red-500', text: 'text-red-600', label: t('common.likelihood.critical', 'Quasi certain') }
      };
      return styles[level] || styles[2];
    };

    const previewStyle = getVraisemblanceStyle(previewValues.vraisemblance);

    return (
      <div className="space-y-4">
        {/* Header with Live Preview */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">{t('workshop4.risk.parameters')}</h3>
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 ${previewStyle.bg} rounded-full animate-pulse`}></div>
            <span className={`text-sm font-bold ${previewStyle.text}`}>
              {previewStyle.label} {previewValues.vraisemblance}/4
            </span>
          </div>
        </div>

        {/* Method Selection - Horizontal Pills */}
        <div className="bg-slate-100 rounded-lg p-1">
          <div className="flex space-x-1">
            {[
              { id: 'express', icon: '⚡', label: t('workshop4.risk.methods.express.label'), desc: t('workshop4.risk.methods.express.help', 'Direct estimation or simple PS×DT') },
              { id: 'standard', icon: '📊', label: t('workshop4.risk.methods.standard.label'), desc: t('workshop4.risk.methods.standard.help', 'V(Mode) = MIN(PS), V(Scénario) = MAX(V modes)') },
              { id: 'advanced', icon: '🎯', label: t('workshop4.risk.methods.advanced.label'), desc: t('workshop4.risk.methods.advanced.help', 'V(Mode) = MATRIX[MAX(DT)][MIN(PS)]') }
            ].map((method) => (
              <button
                key={method.id}
                onClick={() => {
                  setRiskMethod(method.id);
                  setRiskData(getDefaultRiskData(method.id));
                }}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${riskMethod === method.id
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
                  }`}
                title={method.desc}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>{method.icon}</span>
                  <span>{method.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Method Description & Matrix */}
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          {riskMethod === 'express' && (
            <div className="text-xs text-slate-600">
              <div className="font-medium text-slate-700 mb-1">{t('workshop4.risk.methods.express.label')} - {t('workshop4.risk.methods.express.help', 'Quick estimation')}</div>
              <p>{t('workshop4.risk.express.desc', 'Direct V or calculated via MATRIX[DT][PS]')}</p>
            </div>
          )}
          {riskMethod === 'standard' && (
            <div className="text-xs text-slate-600">
              <div className="font-medium text-slate-700 mb-1">{t('workshop4.risk.methods.standard.label')}</div>
              <p className="mb-2">{t('workshop4.risk.standard.desc', 'V(Mode) = MIN(PS of actions), V(Scénario) = MAX(V modes)')}</p>
              <p className="text-[10px] text-slate-500">{t('workshop4.risk.standard.note', 'PS = Probability of Success (1=low, 4=high)')}</p>
            </div>
          )}
          {riskMethod === 'advanced' && (
            <div className="text-xs text-slate-600">
              <div className="font-medium text-slate-700 mb-2">{t('workshop4.risk.methods.advanced.label')}</div>
              <p className="mb-2">{t('workshop4.risk.advanced.desc', 'V(Mode) = MATRIX[MAX(DT)][MIN(PS)]')}</p>
              {/* Vraisemblance Matrix */}
              <div className="bg-slate-50 rounded p-2 border border-slate-200">
                <div className="text-[10px] font-medium text-slate-500 mb-1 text-center">{t('workshop4.risk.matrix.title', 'Vraisemblance Matrix')}</div>
                <div className="grid grid-cols-5 gap-0.5 text-[9px] text-center">
                  {/* Header */}
                  <div className="bg-slate-200 p-1 font-medium">DT\PS</div>
                  <div className="bg-blue-100 p-1 font-medium text-blue-700">1</div>
                  <div className="bg-blue-100 p-1 font-medium text-blue-700">2</div>
                  <div className="bg-blue-100 p-1 font-medium text-blue-700">3</div>
                  <div className="bg-blue-100 p-1 font-medium text-blue-700">4</div>
                  {/* Row 1: DT=1 */}
                  <div className="bg-green-100 p-1 font-medium text-green-700">1</div>
                  <div className="bg-emerald-200 p-1">1</div>
                  <div className="bg-emerald-200 p-1">2</div>
                  <div className="bg-yellow-200 p-1">3</div>
                  <div className="bg-red-200 p-1">4</div>
                  {/* Row 2: DT=2 */}
                  <div className="bg-green-100 p-1 font-medium text-green-700">2</div>
                  <div className="bg-emerald-200 p-1">1</div>
                  <div className="bg-emerald-200 p-1">2</div>
                  <div className="bg-yellow-200 p-1">3</div>
                  <div className="bg-orange-200 p-1">3</div>
                  {/* Row 3: DT=3 */}
                  <div className="bg-green-100 p-1 font-medium text-green-700">3</div>
                  <div className="bg-emerald-200 p-1">1</div>
                  <div className="bg-emerald-200 p-1">1</div>
                  <div className="bg-yellow-200 p-1">2</div>
                  <div className="bg-yellow-200 p-1">3</div>
                  {/* Row 4: DT=4 */}
                  <div className="bg-green-100 p-1 font-medium text-green-700">4</div>
                  <div className="bg-emerald-200 p-1">1</div>
                  <div className="bg-emerald-200 p-1">1</div>
                  <div className="bg-emerald-200 p-1">2</div>
                  <div className="bg-yellow-200 p-1">2</div>
                </div>
                <div className="text-[9px] text-slate-400 mt-1 text-center">
                  PS = {t('workshop4.risk.matrix.ps', 'Probability of Success')} | DT = {t('workshop4.risk.matrix.dt', 'Technical Difficulty')}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Smart Form Based on Method */}
        <div className="bg-slate-50 rounded-lg p-4">
          {riskMethod === 'express' && <SmartExpressForm data={riskData} onChange={setRiskData} />}
          {riskMethod === 'standard' && <SmartStandardForm data={riskData} onChange={setRiskData} scenario={scenario} />}
          {riskMethod === 'advanced' && <SmartAdvancedForm data={riskData} onChange={setRiskData} scenario={scenario} />}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-2">
          <button
            onClick={() => setShowMethodInfo(true)}
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center"
          >
            <Info className="w-3 h-3 mr-1" />
            Aide
          </button>

          <div className="flex space-x-2">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
            >
              Appliquer
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderHorizontalEditForm = () => {
    const previewValues = calculateRiskMetrics(scenario, riskMethod, riskData);
    const getVraisemblanceStyle = (level) => {
      const styles = {
        1: { bg: 'bg-emerald-500', text: 'text-emerald-600', label: t('common.likelihood.low', 'Peu vraisemblable') },
        2: { bg: 'bg-yellow-500', text: 'text-yellow-600', label: t('common.likelihood.medium', 'Vraisemblable') },
        3: { bg: 'bg-orange-500', text: 'text-orange-600', label: t('common.likelihood.high', 'Très vraisemblable') },
        4: { bg: 'bg-red-500', text: 'text-red-600', label: t('common.likelihood.critical', 'Quasi certain') }
      };
      return styles[level] || styles[2];
    };

    const previewStyle = getVraisemblanceStyle(previewValues.vraisemblance);

    return (
      <motion.div
        className="bg-gradient-to-r from-slate-50 via-white to-slate-50 rounded-xl border border-slate-200 p-4"
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Method Selection */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <div className="text-xs font-medium text-slate-700 mb-2">{t('workshop4.risk.calculationMethod')}</div>
            <div className="space-y-1">
              {[
                { id: 'express', icon: '⚡', label: t('workshop4.risk.methods.express.label'), desc: t('workshop4.risk.methods.express.description') },
                { id: 'standard', icon: '📊', label: t('workshop4.risk.methods.standard.label'), desc: t('workshop4.risk.methods.standard.description') },
                { id: 'advanced', icon: '🎯', label: t('workshop4.risk.methods.advanced.label'), desc: t('workshop4.risk.methods.advanced.description') }
              ].map((method) => (
                <button
                  key={method.id}
                  onClick={() => {
                    setRiskMethod(method.id);
                    setRiskData(getDefaultRiskData(method.id));
                  }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-all ${riskMethod === method.id
                    ? 'bg-blue-100 text-blue-800 border border-blue-300'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
                    }`}
                >
                  <span>{method.icon}</span>
                  <div className="flex-1 text-left">
                    <div className="font-medium">{method.label}</div>
                    <div className="text-xs opacity-75">{method.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Parameters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <div className="text-xs font-medium text-slate-700 mb-2">Configuration</div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              {riskMethod === 'express' && <HorizontalExpressForm data={riskData} onChange={setRiskData} />}
              {riskMethod === 'standard' && <HorizontalStandardForm data={riskData} onChange={setRiskData} scenario={scenario} />}
              {riskMethod === 'advanced' && <HorizontalAdvancedForm data={riskData} onChange={setRiskData} scenario={scenario} />}
            </div>
          </motion.div>

          {/* Live Preview */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.3 }}
          >
            <div className="text-xs font-medium text-slate-700 mb-2">{t('workshop4.risk.realtimePreview')}</div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="text-center">
                <div className={`w-8 h-8 ${previewStyle.bg} rounded-full mx-auto mb-2 animate-pulse`}></div>
                <div className={`text-lg font-bold ${previewStyle.text}`}>
                  {previewStyle.label}
                </div>
                <div className="text-sm text-slate-600">{previewValues.vraisemblance}/4</div>
                <div className="text-xs text-slate-500 mt-2">
                  {getCalculationDetails(riskMethod, riskData, previewValues, scenario, t)}
                </div>
              </div>

              {/* Mini metrics */}
              <div className="mt-3 pt-3 border-t border-slate-200 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">{t('workshop4.risk.successProbability')} PS:</span>
                  <span className="font-medium">{previewValues.probability}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Impact:</span>
                  <span className="font-medium">{previewValues.impact}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">{t('workshop4.risk.finalRisk')}:</span>
                  <span className="font-medium">{previewValues.finalRisk}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Action Bar */}
        <motion.div
          className="mt-4 flex items-center justify-between bg-white rounded-lg border border-slate-200 p-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
        >
          <div className="flex items-center space-x-2 text-xs text-slate-500">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>{t('workshop4.risk.automaticCalculations')}</span>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowMethodInfo(true)}
              className="flex items-center px-3 py-1.5 text-xs text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            >
              <Info className="w-3 h-3 mr-1" />
              {t('workshop4.risk.helpMethods')}
            </button>

            <div className="h-4 w-px bg-slate-300"></div>

            <motion.button
              onClick={handleCancel}
              className="flex items-center px-4 py-2 text-sm text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </motion.button>

            <motion.button
              onClick={handleSave}
              className="flex items-center px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg shadow-sm transition-all"
              whileHover={{ scale: 1.02, boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)" }}
              whileTap={{ scale: 0.98 }}
            >
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  if (!scenario) return null;

  return (
    <div className={`${className}`}>
      <AnimatePresence mode="wait">
        {!isEditing ? (
          // Risk Display Mode
          <motion.div
            key="risk-display"
            initial={{ opacity: 0, x: -20, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {/* Risk Display - Edit button is now inside */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              {renderRiskDisplay()}
            </motion.div>
          </motion.div>
        ) : (
          // Parameters Mode - Horizontal Layout
          <motion.div
            key="parameters"
            initial={{ opacity: 0, x: 20, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {/* Header */}
            <motion.div
              className="flex items-center justify-between mb-3"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.2 }}
            >
              <div className="flex items-center space-x-2">
                <motion.div
                  className="w-2 h-2 bg-orange-500 rounded-full"
                  animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <h3 className="text-sm font-medium text-slate-700">{t('workshop4.risk.parameters')}</h3>
              </div>

              <div className="text-xs text-slate-500">
                Mode édition • Modifications en temps réel
              </div>
            </motion.div>

            {/* Horizontal Parameters Form */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              {renderHorizontalEditForm()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Method Info Modal */}
      {showMethodInfo && (
        <MethodInfoModal onClose={() => setShowMethodInfo(false)} />
      )}
    </div>
  );
};

// Smart Express Form - Visual and Intuitive
const SmartExpressForm = ({ data, onChange }) => {
  const { t } = useTranslation();
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="text-sm font-medium text-slate-700 mb-3">{t('riskAssessment.evaluationMode')}</div>
        <div className="flex space-x-3 justify-center">
          <button
            onClick={() => handleChange('estimationMode', 'direct')}
            className={`px-4 py-3 rounded-lg border-2 transition-all ${data.estimationMode === 'direct'
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-slate-200 hover:border-slate-300'
              }`}
          >
            <div className="text-2xl mb-1">🎯</div>
            <div className="text-sm font-medium">{t('riskAssessment.direct')}</div>
          </button>
          <button
            onClick={() => handleChange('estimationMode', 'probability_difficulty')}
            className={`px-4 py-3 rounded-lg border-2 transition-all ${data.estimationMode === 'probability_difficulty'
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-slate-200 hover:border-slate-300'
              }`}
          >
            <div className="text-2xl mb-1">🧮</div>
            <div className="text-sm font-medium">{t('riskAssessment.calculated')}</div>
          </button>
        </div>
      </div>

      {data.estimationMode === 'direct' ? (
        <div>
          <div className="text-sm font-medium text-slate-700 mb-2 text-center">{t('riskAssessment.likelihoodLevel')}</div>
          <div className="flex space-x-2">
            {[
              { value: 1, labelKey: 'low', color: 'emerald', emoji: '🟢' },
              { value: 2, labelKey: 'medium', color: 'yellow', emoji: '🟡' },
              { value: 3, labelKey: 'high', color: 'orange', emoji: '🟠' },
              { value: 4, labelKey: 'critical', color: 'red', emoji: '🔴' }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleChange('globalVraisemblance', option.value)}
                className={`flex-1 p-3 rounded-lg border-2 transition-all ${data.globalVraisemblance === option.value
                  ? `border-${option.color}-500 bg-${option.color}-50`
                  : 'border-slate-200 hover:border-slate-300'
                  }`}
              >
                <div className="text-lg mb-1">{option.emoji}</div>
                <div className="text-xs font-medium">{t(`riskAssessment.riskLevels.${option.labelKey}`)}</div>
                <div className="text-xs text-slate-500">{option.value}/4</div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('riskAssessment.successProbability')}</label>
            <select
              value={data.scenarioProbability || 3}
              onChange={(e) => handleChange('scenarioProbability', parseInt(e.target.value))}
              className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={1}>🟢 {t('riskAssessment.probabilityLevels.veryLow')} ({t('riskAssessment.probabilityLevels.veryLowDesc')})</option>
              <option value={2}>🟡 {t('riskAssessment.probabilityLevels.low')} ({t('riskAssessment.probabilityLevels.lowDesc')})</option>
              <option value={3}>🟠 {t('riskAssessment.probabilityLevels.high')} ({t('riskAssessment.probabilityLevels.highDesc')})</option>
              <option value={4}>🔴 {t('riskAssessment.probabilityLevels.veryHigh')} ({t('riskAssessment.probabilityLevels.veryHighDesc')})</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('riskAssessment.technicalDifficulty')}</label>
            <select
              value={data.scenarioDifficulty || 2}
              onChange={(e) => handleChange('scenarioDifficulty', parseInt(e.target.value))}
              className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={1}>🟢 {t('riskAssessment.difficultyLevels.negligible')}</option>
              <option value={2}>🟡 {t('riskAssessment.difficultyLevels.low')}</option>
              <option value={3}>🟠 {t('riskAssessment.difficultyLevels.moderate')}</option>
              <option value={4}>🔴 {t('riskAssessment.difficultyLevels.high')}</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

// Original SmartStandardForm, now used as a fallback
const _SmartStandardFormLegacy = ({ data, onChange }) => {
  const { t } = useTranslation();
  const phases = [
    { id: 'CONNAITRE', labelKey: 'connaitreLabel', descKey: 'connaitreDesc', icon: '🔍' },
    { id: 'RENTRER', labelKey: 'rentrerLabel', descKey: 'rentrerDesc', icon: '🚪' },
    { id: 'TROUVER', labelKey: 'trouverLabel', descKey: 'trouverDesc', icon: '🎯' },
    { id: 'EXPLOITER', labelKey: 'exploiterLabel', descKey: 'exploiterDesc', icon: '⚡' }
  ];

  const handleStepProbabilityChange = (stepIndex, value) => {
    const newProbabilities = [...(data.stepProbabilities || [3, 3, 3, 3])];
    newProbabilities[stepIndex] = value;
    onChange({ ...data, stepProbabilities: newProbabilities });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">{t('riskAssessment.organizationalDefenseLevel')}</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'low', labelKey: 'low', descKey: 'lowDesc' },
            { value: 'intermediate', labelKey: 'intermediate', descKey: 'intermediateDesc' },
            { value: 'high', labelKey: 'high', descKey: 'highDesc' },
            { value: 'very_high', labelKey: 'veryHigh', descKey: 'veryHighDesc' }
          ].map((level) => (
            <button
              key={level.value}
              onClick={() => onChange({ ...data, defenseLevel: level.value })}
              className={`p-2 rounded-lg border text-left transition-all ${data.defenseLevel === level.value
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-slate-200 hover:border-slate-300'
                }`}
            >
              <div className="text-sm font-medium">{t(`riskAssessment.defenseLevels.${level.labelKey}`)}</div>
              <div className="text-xs text-slate-500">{t(`riskAssessment.defenseLevels.${level.descKey}`)}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-sm font-medium text-slate-700 mb-3">{t('riskAssessment.phaseRating')}</div>
        <div className="space-y-2">
          {phases.map((phase, index) => (
            <div key={phase.id} className="flex items-center space-x-3 bg-white rounded-lg p-2 border">
              <div className="text-lg">{phase.icon}</div>
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-700">{t(`riskAssessment.phases.${phase.labelKey}`)}</div>
                <div className="text-xs text-slate-500">{t(`riskAssessment.phases.${phase.descKey}`)}</div>
              </div>
              <div className="flex space-x-1">
                {[1, 2, 3, 4].map((value) => (
                  <button
                    key={value}
                    onClick={() => handleStepProbabilityChange(index, value)}
                    className={`w-8 h-8 rounded-full text-xs font-bold transition-all ${(data.stepProbabilities?.[index] || 3) === value
                      ? value === 1 ? 'bg-emerald-500 text-white' :
                        value === 2 ? 'bg-yellow-500 text-white' :
                          value === 3 ? 'bg-orange-500 text-white' :
                            'bg-red-500 text-white'
                      : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Enhanced Standard Form that supports Elementary Actions (Compact UI)
const SmartStandardForm = ({ data, onChange, scenario }) => {
  const { t } = useTranslation();
  const [expandedPhases, setExpandedPhases] = useState({});
  const [selectedModeIndex, setSelectedModeIndex] = useState(null);

  // Mode colors for visual distinction (same as Advanced)
  const modeColors = [
    { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-500', ring: 'ring-blue-400' },
    { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-500', ring: 'ring-emerald-400' },
    { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-500', ring: 'ring-amber-400' },
    { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-500', ring: 'ring-pink-400' },
    { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-500', ring: 'ring-indigo-400' },
    { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-500', ring: 'ring-cyan-400' },
  ];

  // Get elementary actions if available, otherwise fall back to phases
  const elementaryActions = data.elementaryActions || scenario?.elementaryActions || scenario?.operationalSteps || [];
  const hasElementaryActions = elementaryActions.length > 0 && elementaryActions.some(a => a.elementaryActionId);

  // Group actions by phase
  const actionsByPhase = {
    CONNAITRE: elementaryActions.filter(a => a.phase === 'CONNAITRE'),
    RENTRER: elementaryActions.filter(a => a.phase === 'RENTRER'),
    TROUVER: elementaryActions.filter(a => a.phase === 'TROUVER'),
    EXPLOITER: elementaryActions.filter(a => a.phase === 'EXPLOITER')
  };

  const phaseInfo = {
    CONNAITRE: { icon: '🔍', label: 'CONN', color: 'blue' },
    RENTRER: { icon: '🚪', label: 'RENT', color: 'red' },
    TROUVER: { icon: '🎯', label: 'TROU', color: 'amber' },
    EXPLOITER: { icon: '⚡', label: 'EXPL', color: 'green' }
  };

  const togglePhase = (phase) => {
    setExpandedPhases(prev => ({ ...prev, [phase]: !prev[phase] }));
  };

  const handleActionPSChange = (actionId, value) => {
    const updatedActions = elementaryActions.map(action => {
      if (action.elementaryActionId === actionId) {
        return { ...action, successProbability: value, ps: value };
      }
      return action;
    });
    onChange({ ...data, elementaryActions: updatedActions });
  };

  // Calculate phase summary (MIN PS of all actions in phase)
  const getPhaseMinPS = (actions) => {
    if (actions.length === 0) return null;
    const psValues = actions.map(a => a.successProbability || a.ps || 2).filter(v => v >= 1 && v <= 4);
    return psValues.length > 0 ? Math.min(...psValues) : 2;
  };

  // Calculate mode V values for display (Standard: V = MIN(PS))
  const calculateModeV = (mode) => {
    const modeActionIds = mode.path || [];
    const modeActions = modeActionIds
      .map(id => elementaryActions.find(a => a.elementaryActionId === id))
      .filter(Boolean);
    if (modeActions.length === 0) return mode.vraisemblance || 2;
    const psValues = modeActions.map(a => a.successProbability || a.ps || 2).filter(v => v >= 1 && v <= 4);
    return psValues.length > 0 ? Math.min(...psValues) : 2;
  };

  if (!hasElementaryActions) {
    return <_SmartStandardFormLegacy data={data} onChange={onChange} />;
  }

  // Calculate scenario V from modesOperatoires (the correct EBIOS RM way)
  const modes = scenario?.modesOperatoires || data.modes || [];
  const scenarioV = modes.length > 0
    ? Math.max(...modes.map(m => calculateModeV(m)))
    : Math.min(...elementaryActions.map(a => a.successProbability || a.ps || 2).filter(v => v >= 1 && v <= 4));

  return (
    <div className="space-y-2">
      {/* Compact Header with Live V */}
      <div className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2 border border-blue-200">
        <div>
          <div className="text-xs font-semibold text-blue-800">📊 {t('riskAssessment.standardMode')}</div>
          <div className="text-[10px] text-blue-600">{t('riskAssessment.formulaMinPS')}</div>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-bold ${scenarioV >= 3 ? 'bg-red-100 text-red-700' :
          scenarioV === 2 ? 'bg-yellow-100 text-yellow-700' :
            'bg-emerald-100 text-emerald-700'
          }`}>
          V = {scenarioV}
        </div>
      </div>

      {/* Compact Phase Grid */}
      <div className="max-h-[300px] overflow-y-auto pr-1 space-y-1">
        {Object.entries(actionsByPhase).map(([phase, actions]) => {
          if (actions.length === 0) return null;
          const info = phaseInfo[phase];
          const isExpanded = expandedPhases[phase];
          const minPS = getPhaseMinPS(actions);

          return (
            <div key={phase} className="bg-white rounded border border-slate-200 overflow-hidden">
              {/* Collapsible Phase Header - Simple */}
              <button
                onClick={() => togglePhase(phase)}
                className="w-full px-2 py-1.5 bg-slate-50 flex items-center justify-between hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">{info.icon}</span>
                  <span className="text-xs font-semibold text-slate-700">{info.label}</span>
                  <span className="text-[10px] text-slate-400">({actions.length})</span>
                </div>
                <span className="text-slate-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
              </button>

              {/* Expanded Actions - Vertical Layout */}
              {isExpanded && (
                <div className="p-2 space-y-1 bg-slate-25">
                  {actions.map((action) => {
                    const ps = action.successProbability || action.ps || 2;

                    // Check if this action is in the selected mode
                    const selectedMode = selectedModeIndex !== null ? modes[selectedModeIndex] : null;
                    const isInSelectedMode = selectedMode && (selectedMode.path || []).includes(action.elementaryActionId);
                    const selectedColor = selectedModeIndex !== null ? modeColors[selectedModeIndex % modeColors.length] : null;

                    return (
                      <div
                        key={action.elementaryActionId}
                        className={`flex items-center gap-2 py-0.5 px-1 rounded transition-all ${isInSelectedMode
                          ? `border-l-4 ${selectedColor.border} ${selectedColor.bg} bg-opacity-50`
                          : ''
                          }`}
                      >
                        <div className="flex-1 min-w-0 text-xs text-slate-600 truncate" title={action.name || action.technique}>
                          {(action.name || action.technique || 'Action').substring(0, 25)}
                        </div>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4].map((value) => (
                            <button
                              key={value}
                              onClick={() => handleActionPSChange(action.elementaryActionId, value)}
                              className={`w-5 h-5 rounded text-[10px] font-bold transition-all ${ps === value
                                ? value <= 2 ? 'bg-emerald-500 text-white' :
                                  value === 3 ? 'bg-orange-500 text-white' : 'bg-red-500 text-white'
                                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                }`}
                            >
                              {value}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selectable Modes Summary */}
      {modes.length > 0 && (
        <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
          <div className="text-[10px] text-slate-500 mb-1">
            {t('workshop4.risk.clickToHighlight', 'Click a mode to highlight its actions')}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {modes.map((mode, idx) => {
              const modeV = calculateModeV(mode);
              const color = modeColors[idx % modeColors.length];
              const isSelected = selectedModeIndex === idx;
              return (
                <button
                  key={mode.id || idx}
                  onClick={() => setSelectedModeIndex(isSelected ? null : idx)}
                  className={`px-2 py-1 rounded text-xs font-medium cursor-pointer transition-all ${color.bg} ${color.text} ${isSelected ? `ring-2 ${color.ring} shadow-md scale-105` : 'hover:scale-102 hover:shadow-sm'
                    }`}
                >
                  Mode {idx + 1}: V={modeV}
                </button>
              );
            })}
            <span className="text-[10px] text-purple-600 ml-auto">→ MAX = {scenarioV}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Original SmartAdvancedForm, now used as a fallback
const _SmartAdvancedFormLegacy = ({ data, onChange }) => {
  const phases = [
    { id: 'CONNAITRE', label: 'Connaître', icon: '🔍' },
    { id: 'RENTRER', label: 'Rentrer', icon: '🚪' },
    { id: 'TROUVER', label: 'Trouver', icon: '🎯' },
    { id: 'EXPLOITER', label: 'Exploiter', icon: '⚡' }
  ];

  const handleStepChange = (stepIndex, field, value) => {
    const currentData = { ...data };
    if (!currentData[field]) {
      currentData[field] = [3, 3, 3, 3];
    }
    const newValues = [...currentData[field]];
    newValues[stepIndex] = value;
    onChange({ ...currentData, [field]: newValues });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Niveau de défense</label>
        <select
          value={data.defenseLevel || 'intermediate'}
          onChange={(e) => onChange({ ...data, defenseLevel: e.target.value })}
          className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="low">🔓 Faible</option>
          <option value="intermediate">🔒 Intermédiaire</option>
          <option value="high">🛡️ Élevé</option>
          <option value="very_high">🏰 Très Élevé</option>
        </select>
      </div>

      <div>
        <div className="text-sm font-medium text-slate-700 mb-3">Double cotation par phase</div>
        <div className="space-y-3">
          {phases.map((phase, index) => {
            const probability = data.stepProbabilities?.[index] || 3;
            const difficulty = data.stepDifficulties?.[index] || 2;
            const vraisemblance = calculateVraisemblance(probability, difficulty);

            return (
              <div key={phase.id} className="bg-white rounded-lg p-3 border">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{phase.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{phase.label}</span>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-bold ${vraisemblance === 4 ? 'bg-red-100 text-red-700' :
                    vraisemblance === 3 ? 'bg-orange-100 text-orange-700' :
                      vraisemblance === 2 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-emerald-100 text-emerald-700'
                    }`}>
                    Résultat: {vraisemblance}/4
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Probabilité</label>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4].map((value) => (
                        <button
                          key={value}
                          onClick={() => handleStepChange(index, 'stepProbabilities', value)}
                          className={`flex-1 py-1 text-xs rounded ${probability === value
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                            }`}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Difficulté technique DT</label>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4].map((value) => (
                        <button
                          key={value}
                          onClick={() => handleStepChange(index, 'stepDifficulties', value)}
                          className={`flex-1 py-1 text-xs rounded ${difficulty === value
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                            }`}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Enhanced Advanced Form that supports Elementary Actions with PS + DT (Compact UI)
const SmartAdvancedForm = ({ data, onChange, scenario }) => {
  const { t } = useTranslation();
  const [expandedPhases, setExpandedPhases] = useState({});
  const [selectedModeIndex, setSelectedModeIndex] = useState(null);

  // Mode colors for visual distinction
  const modeColors = [
    { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-500', ring: 'ring-blue-400' },
    { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-500', ring: 'ring-emerald-400' },
    { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-500', ring: 'ring-amber-400' },
    { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-500', ring: 'ring-pink-400' },
    { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-500', ring: 'ring-indigo-400' },
    { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-500', ring: 'ring-cyan-400' },
  ];

  // Get elementary actions if available
  const elementaryActions = data.elementaryActions || scenario?.elementaryActions || scenario?.operationalSteps || [];
  const hasElementaryActions = elementaryActions.length > 0 && elementaryActions.some(a => a.elementaryActionId);

  // Group actions by phase
  const actionsByPhase = {
    CONNAITRE: elementaryActions.filter(a => a.phase === 'CONNAITRE'),
    RENTRER: elementaryActions.filter(a => a.phase === 'RENTRER'),
    TROUVER: elementaryActions.filter(a => a.phase === 'TROUVER'),
    EXPLOITER: elementaryActions.filter(a => a.phase === 'EXPLOITER')
  };

  const phaseInfo = {
    CONNAITRE: { icon: '🔍', label: 'CONN', color: 'blue' },
    RENTRER: { icon: '🚪', label: 'RENT', color: 'red' },
    TROUVER: { icon: '🎯', label: 'TROU', color: 'amber' },
    EXPLOITER: { icon: '⚡', label: 'EXPL', color: 'green' }
  };

  const togglePhase = (phase) => {
    setExpandedPhases(prev => ({ ...prev, [phase]: !prev[phase] }));
  };

  const handleActionChange = (actionId, field, value) => {
    const updatedActions = elementaryActions.map(action => {
      if (action.elementaryActionId === actionId) {
        const updated = { ...action };
        if (field === 'ps') {
          updated.successProbability = value;
          updated.ps = value;
        } else if (field === 'dt') {
          updated.technicalDifficulty = value;
          updated.dt = value;
        }
        const ps = field === 'ps' ? value : (updated.successProbability || updated.ps || 2);
        const dt = field === 'dt' ? value : (updated.technicalDifficulty || updated.dt || 2);
        updated.vraisemblance = calculateVraisemblance(ps, dt);
        return updated;
      }
      return action;
    });
    onChange({ ...data, elementaryActions: updatedActions });
  };

  // Get phase summary with EBIOS formula
  const getPhaseStats = (actions) => {
    if (actions.length === 0) return { minPS: null, maxDT: null, v: null };
    const psValues = actions.map(a => a.successProbability || a.ps || 2).filter(v => v >= 1 && v <= 4);
    const dtValues = actions.map(a => a.technicalDifficulty || a.dt || 2).filter(v => v >= 1 && v <= 4);
    const minPS = psValues.length > 0 ? Math.min(...psValues) : 2;
    const maxDT = dtValues.length > 0 ? Math.max(...dtValues) : 2;
    return { minPS, maxDT, v: calculateVraisemblance(minPS, maxDT) };
  };

  // Calculate mode V values
  const calculateModeV = (mode) => {
    const modeActionIds = mode.path || [];
    const modeActions = modeActionIds
      .map(id => elementaryActions.find(a => a.elementaryActionId === id))
      .filter(Boolean);
    if (modeActions.length === 0) return mode.vraisemblance || 2;
    const psValues = modeActions.map(a => a.successProbability || a.ps || 2).filter(v => v >= 1 && v <= 4);
    const dtValues = modeActions.map(a => a.technicalDifficulty || a.dt || 2).filter(v => v >= 1 && v <= 4);
    if (psValues.length === 0) return 2;
    const minPS = Math.min(...psValues);
    const maxDT = dtValues.length > 0 ? Math.max(...dtValues) : 2;
    return calculateVraisemblance(minPS, maxDT);
  };

  if (!hasElementaryActions) {
    return <_SmartAdvancedFormLegacy data={data} onChange={onChange} />;
  }

  // Calculate scenario V from modesOperatoires (the correct EBIOS RM way)
  const modes = scenario?.modesOperatoires || data.modes || [];
  const scenarioV = modes.length > 0
    ? Math.max(...modes.map(m => calculateModeV(m)))
    : (() => {
      // Fallback: calculate from all actions if no modes defined
      const psVals = elementaryActions.map(a => a.successProbability || a.ps || 2).filter(v => v >= 1 && v <= 4);
      const dtVals = elementaryActions.map(a => a.technicalDifficulty || a.dt || 2).filter(v => v >= 1 && v <= 4);
      return psVals.length > 0 ? calculateVraisemblance(Math.min(...psVals), Math.max(...dtVals)) : 2;
    })();

  // Calculate each mode's V for display
  const modeVValues = modes.map((mode, idx) => ({
    name: `Mode ${idx + 1}`,
    v: calculateModeV(mode)
  }));

  return (
    <div className="space-y-2">
      {/* Compact Header with Live V */}
      <div className="flex items-center justify-between bg-purple-50 rounded-lg px-3 py-2 border border-purple-200">
        <div>
          <div className="text-xs font-semibold text-purple-800">🎯 {t('riskAssessment.advancedMode')}</div>
          <div className="text-[10px] text-purple-600">{t('riskAssessment.formulaMatrix')}</div>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-bold ${scenarioV >= 3 ? 'bg-red-100 text-red-700' :
          scenarioV === 2 ? 'bg-yellow-100 text-yellow-700' :
            'bg-emerald-100 text-emerald-700'
          }`}>
          V = {scenarioV}
        </div>
      </div>

      {/* Modes V Breakdown - Selectable */}
      {modes.length > 0 && (
        <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
          <div className="text-[10px] text-slate-500 mb-1">
            {t('workshop4.risk.clickToHighlight', 'Click a mode to highlight its actions')}
          </div>
          <div className="flex flex-wrap gap-2">
            {modeVValues.map((mode, idx) => {
              const color = modeColors[idx % modeColors.length];
              const isSelected = selectedModeIndex === idx;
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedModeIndex(isSelected ? null : idx)}
                  className={`px-2 py-1 rounded text-xs font-medium cursor-pointer transition-all ${color.bg} ${color.text} ${isSelected ? `ring-2 ${color.ring} shadow-md scale-105` : 'hover:scale-102 hover:shadow-sm'
                    }`}
                >
                  Mode {idx + 1}: V={mode.v}
                </button>
              );
            })}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            V(Scénario) = MAX({modeVValues.map(m => m.v).join(', ')}) = <span className="font-bold text-purple-700">{scenarioV}</span>
          </div>
        </div>
      )}

      {/* Compact Phase Grid with Scroll */}
      <div className="max-h-[320px] overflow-y-auto pr-1 space-y-1">
        {Object.entries(actionsByPhase).map(([phase, actions]) => {
          if (actions.length === 0) return null;
          const info = phaseInfo[phase];
          const isExpanded = expandedPhases[phase];
          const stats = getPhaseStats(actions);

          return (
            <div key={phase} className="bg-white rounded border border-slate-200 overflow-hidden">
              {/* Collapsible Phase Header */}
              <button
                onClick={() => togglePhase(phase)}
                className="w-full px-2 py-1.5 bg-slate-50 flex items-center justify-between hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">{info.icon}</span>
                  <span className="text-xs font-semibold text-slate-700">{info.label}</span>
                  <span className="text-[10px] text-slate-400">({actions.length})</span>
                </div>
                <span className="text-slate-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
              </button>

              {/* Expanded Actions - Vertical Layout */}
              {isExpanded && (
                <div className="p-2 space-y-1 bg-slate-25">
                  {actions.map((action) => {
                    const ps = action.successProbability || action.ps || 2;
                    const dt = action.technicalDifficulty || action.dt || 2;
                    const actionV = calculateVraisemblance(ps, dt);

                    // Check if this action is in the selected mode
                    const selectedMode = selectedModeIndex !== null ? modes[selectedModeIndex] : null;
                    const isInSelectedMode = selectedMode && (selectedMode.path || []).includes(action.elementaryActionId);
                    const selectedColor = selectedModeIndex !== null ? modeColors[selectedModeIndex % modeColors.length] : null;

                    return (
                      <div
                        key={action.elementaryActionId}
                        className={`flex items-center gap-2 py-0.5 px-1 rounded transition-all ${isInSelectedMode
                          ? `border-l-4 ${selectedColor.border} ${selectedColor.bg} bg-opacity-50`
                          : ''
                          }`}
                      >
                        <div className="flex-1 min-w-0 text-xs text-slate-600 truncate" title={action.name || action.technique}>
                          {(action.name || action.technique || 'Action').substring(0, 18)}
                        </div>

                        {/* PS buttons */}
                        <div className="flex items-center gap-0.5">
                          <span className="text-[9px] text-blue-500 w-4">PS</span>
                          {[1, 2, 3, 4].map((value) => (
                            <button
                              key={value}
                              onClick={() => handleActionChange(action.elementaryActionId, 'ps', value)}
                              className={`w-4 h-4 rounded text-[9px] font-bold transition-all ${ps === value ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                }`}
                            >
                              {value}
                            </button>
                          ))}
                        </div>

                        {/* DT buttons */}
                        <div className="flex items-center gap-0.5">
                          <span className="text-[9px] text-green-500 w-4">DT</span>
                          {[1, 2, 3, 4].map((value) => (
                            <button
                              key={value}
                              onClick={() => handleActionChange(action.elementaryActionId, 'dt', value)}
                              className={`w-4 h-4 rounded text-[9px] font-bold transition-all ${dt === value ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                }`}
                            >
                              {value}
                            </button>
                          ))}
                        </div>

                        {/* V badge */}
                        <span className={`px-1 py-0.5 rounded text-[9px] font-bold min-w-[20px] text-center ${actionV >= 3 ? 'bg-orange-100 text-orange-700' :
                          actionV === 2 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                          {actionV}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Compact Modes Summary */}
      {data.modes && data.modes.length > 0 && (
        <div className="bg-indigo-50 rounded px-2 py-1.5 border border-indigo-200">
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] font-medium text-indigo-700">{t('riskAssessment.modes')}:</span>
            {data.modes.map((mode, idx) => {
              const modeV = calculateModeV(mode);
              const modeActionIds = mode.path || [];
              const modeActions = modeActionIds.map(id => elementaryActions.find(a => a.elementaryActionId === id)).filter(Boolean);
              const psVals = modeActions.map(a => a.successProbability || a.ps || 2).filter(v => v >= 1 && v <= 4);
              const dtVals = modeActions.map(a => a.technicalDifficulty || a.dt || 2).filter(v => v >= 1 && v <= 4);
              const minPS = psVals.length > 0 ? Math.min(...psVals) : 2;
              const maxDT = dtVals.length > 0 ? Math.max(...dtVals) : 2;

              return (
                <div key={mode.id || idx} className="flex items-center gap-1 bg-white rounded px-1.5 py-0.5 border border-indigo-100">
                  <span className="text-[9px] text-indigo-600 truncate max-w-[50px]" title={mode.name}>
                    {(mode.name || `M${idx + 1}`).substring(0, 6)}
                  </span>
                  <span className="text-[8px] text-slate-400">[{maxDT},{minPS}]</span>
                  <span className={`px-1 rounded text-[9px] font-bold ${modeV >= 3 ? 'bg-orange-100 text-orange-700' :
                    modeV === 2 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                    {modeV}
                  </span>
                </div>
              );
            })}
            <span className="text-[10px] text-indigo-600 ml-auto font-medium">→ MAX = {scenarioV}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Horizontal Forms - Compact for inline editing
const HorizontalExpressForm = ({ data, onChange }) => {
  const { t } = useTranslation();
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs text-slate-600 mb-2">{t('riskAssessment.evaluationMode')}</div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleChange('estimationMode', 'direct')}
            className={`flex-1 px-2 py-1 text-xs rounded ${data.estimationMode === 'direct'
              ? 'bg-blue-100 text-blue-700 border border-blue-300'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            🎯 {t('riskAssessment.direct')}
          </button>
          <button
            onClick={() => handleChange('estimationMode', 'probability_difficulty')}
            className={`flex-1 px-2 py-1 text-xs rounded ${data.estimationMode === 'probability_difficulty'
              ? 'bg-blue-100 text-blue-700 border border-blue-300'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            🧮 {t('riskAssessment.calculated')}
          </button>
        </div>
      </div>

      {data.estimationMode === 'direct' ? (
        <div>
          <div className="text-xs text-slate-600 mb-2">{t('riskAssessment.likelihoodLevel')}</div>
          <div className="flex space-x-1">
            {[
              { value: 1, emoji: '🟢' },
              { value: 2, emoji: '🟡' },
              { value: 3, emoji: '🟠' },
              { value: 4, emoji: '🔴' }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleChange('globalVraisemblance', option.value)}
                className={`flex-1 p-2 text-xs rounded transition-all ${data.globalVraisemblance === option.value
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                <div>{option.emoji}</div>
                <div>{option.value}</div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div>
            <div className="text-xs text-slate-600 mb-1">{t('riskAssessment.successProbabilityPS')}</div>
            <select
              value={data.scenarioProbability || 3}
              onChange={(e) => handleChange('scenarioProbability', parseInt(e.target.value))}
              className="w-full p-1 border border-slate-300 rounded text-xs"
            >
              <option value={1}>🟢 {t('riskAssessment.probabilityLevels.veryLow')}</option>
              <option value={2}>🟡 {t('riskAssessment.probabilityLevels.low')}</option>
              <option value={3}>🟠 {t('riskAssessment.probabilityLevels.high')}</option>
              <option value={4}>🔴 {t('riskAssessment.probabilityLevels.veryHigh')}</option>
            </select>
          </div>
          <div>
            <div className="text-xs text-slate-600 mb-1">{t('riskAssessment.technicalDifficultyDT')}</div>
            <select
              value={data.scenarioDifficulty || 2}
              onChange={(e) => handleChange('scenarioDifficulty', parseInt(e.target.value))}
              className="w-full p-1 border border-slate-300 rounded text-xs"
            >
              <option value={1}>🟢 {t('riskAssessment.difficultyLevels.negligible')}</option>
              <option value={2}>🟡 {t('riskAssessment.difficultyLevels.low')}</option>
              <option value={3}>🟠 {t('riskAssessment.difficultyLevels.moderate')}</option>
              <option value={4}>🔴 {t('riskAssessment.difficultyLevels.high')}</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

const HorizontalStandardForm = ({ data, onChange, scenario }) => {
  const { t } = useTranslation();
  // Check if scenario has elementary actions - if so, use the enhanced form
  const elementaryActions = data.elementaryActions || scenario?.elementaryActions || [];
  const hasElementaryActions = elementaryActions.length > 0 && elementaryActions.some(a => a.elementaryActionId);

  if (hasElementaryActions) {
    return <SmartStandardForm data={data} onChange={onChange} scenario={scenario} />;
  }

  // Legacy phase-based form
  const phases = ['CONN', 'RENT', 'TROU', 'EXPL'];

  const handleStepProbabilityChange = (stepIndex, value) => {
    const newProbabilities = [...(data.stepProbabilities || [3, 3, 3, 3])];
    newProbabilities[stepIndex] = value;
    onChange({ ...data, stepProbabilities: newProbabilities });
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs text-slate-600 mb-2">{t('riskAssessment.defenseLevel')}</div>
        <select
          value={data.defenseLevel || 'intermediate'}
          onChange={(e) => onChange({ ...data, defenseLevel: e.target.value })}
          className="w-full p-1 border border-slate-300 rounded text-xs"
        >
          <option value="low">🔓 {t('riskAssessment.defenseLevels.low')}</option>
          <option value="intermediate">🔒 {t('riskAssessment.defenseLevels.intermediate')}</option>
          <option value="high">🛡️ {t('riskAssessment.defenseLevels.high')}</option>
          <option value="very_high">🏰 {t('riskAssessment.defenseLevels.veryHigh')}</option>
        </select>
      </div>

      <div>
        <div className="text-xs text-slate-600 mb-2">{t('riskAssessment.successProbabilityPS')}</div>
        <div className="grid grid-cols-4 gap-1">
          {phases.map((phase, index) => (
            <div key={phase} className="text-center">
              <div className="text-xs text-slate-600 mb-1">{phase}</div>
              <div className="flex flex-col space-y-1">
                {[1, 2, 3, 4].map((value) => (
                  <button
                    key={value}
                    onClick={() => handleStepProbabilityChange(index, value)}
                    className={`w-full py-1 text-xs rounded ${(data.stepProbabilities?.[index] || 3) === value
                      ? 'bg-blue-100 text-blue-700 border border-blue-300'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const HorizontalAdvancedForm = ({ data, onChange, scenario }) => {
  const { t } = useTranslation();
  // Check if scenario has elementary actions - if so, use the enhanced form
  const elementaryActions = data.elementaryActions || scenario?.elementaryActions || [];
  const hasElementaryActions = elementaryActions.length > 0 && elementaryActions.some(a => a.elementaryActionId);

  if (hasElementaryActions) {
    return <SmartAdvancedForm data={data} onChange={onChange} scenario={scenario} />;
  }

  // Legacy phase-based form
  const phases = ['CONN', 'RENT', 'TROU', 'EXPL'];

  const handleStepChange = (stepIndex, field, value) => {
    const currentData = { ...data };
    if (!currentData[field]) {
      currentData[field] = [3, 3, 3, 3];
    }
    const newValues = [...currentData[field]];
    newValues[stepIndex] = value;
    onChange({ ...currentData, [field]: newValues });
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs text-slate-600 mb-2">{t('riskAssessment.defenseLevel')}</div>
        <select
          value={data.defenseLevel || 'intermediate'}
          onChange={(e) => onChange({ ...data, defenseLevel: e.target.value })}
          className="w-full p-1 border border-slate-300 rounded text-xs"
        >
          <option value="low">🔓 {t('riskAssessment.defenseLevels.low')}</option>
          <option value="intermediate">🔒 {t('riskAssessment.defenseLevels.intermediate')}</option>
          <option value="high">🛡️ {t('riskAssessment.defenseLevels.high')}</option>
          <option value="very_high">🏰 {t('riskAssessment.defenseLevels.veryHigh')}</option>
        </select>
      </div>

      <div>
        <div className="text-xs text-slate-600 mb-2">{t('riskAssessment.doubleRating')}</div>
        <div className="space-y-2">
          {phases.map((phase, index) => {
            const probability = data.stepProbabilities?.[index] || 3;
            const difficulty = data.stepDifficulties?.[index] || 2;
            const vraisemblance = calculateVraisemblance(probability, difficulty);

            return (
              <div key={phase} className="flex items-center space-x-2">
                <div className="text-xs font-medium text-slate-700 w-8">{phase}</div>
                <div className="flex space-x-1">
                  <div className="text-xs text-slate-500">PS:</div>
                  {[1, 2, 3, 4].map((value) => (
                    <button
                      key={value}
                      onClick={() => handleStepChange(index, 'stepProbabilities', value)}
                      className={`w-6 h-6 text-xs rounded ${probability === value
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                        }`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <div className="flex space-x-1">
                  <div className="text-xs text-slate-500">DT:</div>
                  {[1, 2, 3, 4].map((value) => (
                    <button
                      key={value}
                      onClick={() => handleStepChange(index, 'stepDifficulties', value)}
                      className={`w-6 h-6 text-xs rounded ${difficulty === value
                        ? 'bg-green-500 text-white'
                        : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                        }`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <div className={`px-2 py-1 rounded text-xs font-bold ${vraisemblance === 4 ? 'bg-red-100 text-red-700' :
                  vraisemblance === 3 ? 'bg-orange-100 text-orange-700' :
                    vraisemblance === 2 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-emerald-100 text-emerald-700'
                  }`}>
                  {vraisemblance}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Legacy Compact Express Method Form Component
const CompactExpressForm = ({ data, onChange, preview }) => {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-4">
        <label className="flex items-center text-sm">
          <input
            type="radio"
            name="estimationMode"
            value="direct"
            checked={data.estimationMode === 'direct'}
            onChange={(e) => handleChange('estimationMode', e.target.value)}
            className="mr-2"
          />
          Direct
        </label>
        <label className="flex items-center text-sm">
          <input
            type="radio"
            name="estimationMode"
            value="probability_difficulty"
            checked={data.estimationMode === 'probability_difficulty'}
            onChange={(e) => handleChange('estimationMode', e.target.value)}
            className="mr-2"
          />
          Prob × Diff
        </label>
      </div>

      {data.estimationMode === 'direct' ? (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Vraisemblance</label>
          <div className="flex space-x-1">
            {[1, 2, 3, 4].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => handleChange('globalVraisemblance', value)}
                className={`flex-1 p-2 text-xs rounded ${data.globalVraisemblance === value
                  ? getVraisemblanceColor(value)
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Probabilité</label>
            <select
              value={data.scenarioProbability || 3}
              onChange={(e) => handleChange('scenarioProbability', parseInt(e.target.value))}
              className="w-full p-1.5 border border-gray-300 rounded text-xs"
            >
              <option value={1}>Très Faible</option>
              <option value={2}>Faible</option>
              <option value={3}>Élevée</option>
              <option value={4}>Très Élevée</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Difficulté</label>
            <select
              value={data.scenarioDifficulty || 2}
              onChange={(e) => handleChange('scenarioDifficulty', parseInt(e.target.value))}
              className="w-full p-1.5 border border-gray-300 rounded text-xs"
            >
              <option value={1}>Négligeable</option>
              <option value={2}>Faible</option>
              <option value={3}>Modérée</option>
              <option value={4}>Élevée</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

const CompactStandardForm = ({ data, onChange, preview }) => {
  const phases = ['CONNAITRE', 'RENTRER', 'TROUVER', 'EXPLOITER'];

  const handleStepProbabilityChange = (stepIndex, value) => {
    const newProbabilities = [...(data.stepProbabilities || [3, 3, 3, 3])];
    newProbabilities[stepIndex] = value;
    onChange({ ...data, stepProbabilities: newProbabilities });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Niveau de défense</label>
        <select
          value={data.defenseLevel || 'intermediate'}
          onChange={(e) => onChange({ ...data, defenseLevel: e.target.value })}
          className="w-full p-1.5 border border-gray-300 rounded text-xs"
        >
          <option value="low">Faible</option>
          <option value="intermediate">Intermédiaire</option>
          <option value="high">Élevé</option>
          <option value="very_high">Très Élevé</option>
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">Probabilités par phase</label>
        <div className="grid grid-cols-4 gap-1">
          {phases.map((phase, index) => (
            <div key={phase} className="text-center">
              <div className="text-xs text-gray-600 mb-1">{phase.slice(0, 4)}</div>
              <select
                value={data.stepProbabilities?.[index] || 3}
                onChange={(e) => handleStepProbabilityChange(index, parseInt(e.target.value))}
                className="w-full p-1 border border-gray-300 rounded text-xs"
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CompactAdvancedForm = ({ data, onChange, preview }) => {
  const phases = ['CONNAITRE', 'RENTRER', 'TROUVER', 'EXPLOITER'];

  const handleStepChange = (stepIndex, field, value) => {
    const currentData = { ...data };
    if (!currentData[field]) {
      currentData[field] = [3, 3, 3, 3];
    }
    const newValues = [...currentData[field]];
    newValues[stepIndex] = value;
    onChange({ ...currentData, [field]: newValues });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Niveau de défense</label>
        <select
          value={data.defenseLevel || 'intermediate'}
          onChange={(e) => onChange({ ...data, defenseLevel: e.target.value })}
          className="w-full p-1.5 border border-gray-300 rounded text-xs"
        >
          <option value="low">Faible</option>
          <option value="intermediate">Intermédiaire</option>
          <option value="high">Élevé</option>
          <option value="very_high">Très Élevé</option>
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">Double cotation par phase</label>
        <div className="space-y-2">
          {phases.map((phase, index) => {
            const probability = data.stepProbabilities?.[index] || 3;
            const difficulty = data.stepDifficulties?.[index] || 2;
            const vraisemblance = calculateVraisemblance(probability, difficulty);

            return (
              <div key={phase} className="flex items-center space-x-2 bg-gray-50 rounded p-2">
                <div className="text-xs font-medium text-gray-700 w-16">{phase.slice(0, 4)}</div>
                <div className="flex-1 grid grid-cols-2 gap-1">
                  <select
                    value={probability}
                    onChange={(e) => handleStepChange(index, 'stepProbabilities', parseInt(e.target.value))}
                    className="p-1 border border-gray-300 rounded text-xs"
                  >
                    <option value={1}>P:1</option>
                    <option value={2}>P:2</option>
                    <option value={3}>P:3</option>
                    <option value={4}>P:4</option>
                  </select>
                  <select
                    value={difficulty}
                    onChange={(e) => handleStepChange(index, 'stepDifficulties', parseInt(e.target.value))}
                    className="p-1 border border-gray-300 rounded text-xs"
                  >
                    <option value={1}>D:1</option>
                    <option value={2}>D:2</option>
                    <option value={3}>D:3</option>
                    <option value={4}>D:4</option>
                  </select>
                </div>
                <div className={`px-2 py-1 rounded text-xs font-bold ${getVraisemblanceColor(vraisemblance)}`}>
                  {vraisemblance}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Express Method Form Component (Legacy)
const ExpressMethodForm = ({ data, onChange }) => {
  const vraisemblanceOptions = [
    { value: 1, label: 'Faible', color: 'bg-green-100 text-green-800' },
    { value: 2, label: 'Moyenne', color: 'bg-yellow-100 text-yellow-800' },
    { value: 3, label: 'Élevée', color: 'bg-orange-100 text-orange-800' },
    { value: 4, label: 'Critique', color: 'bg-red-100 text-red-800' }
  ];

  const handleChange = (field, value) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Mode d'estimation
        </label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              name="estimationMode"
              value="direct"
              checked={data.estimationMode === 'direct'}
              onChange={(e) => handleChange('estimationMode', e.target.value)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Définir directement le niveau de vraisemblance</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="estimationMode"
              value="probability_difficulty"
              checked={data.estimationMode === 'probability_difficulty'}
              onChange={(e) => handleChange('estimationMode', e.target.value)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Estimer la probabilité de succès et la difficulté technique</span>
          </label>
        </div>
      </div>

      {data.estimationMode === 'direct' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Niveau de vraisemblance global
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {vraisemblanceOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleChange('globalVraisemblance', option.value)}
                className={`p-2 rounded-lg text-xs font-medium transition-all ${data.globalVraisemblance === option.value
                  ? option.color + ' ring-2 ring-offset-2 ring-blue-500'
                  : 'bg-white border border-gray-200 hover:border-gray-300'
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {data.estimationMode === 'probability_difficulty' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Probabilité de succès
            </label>
            <select
              value={data.scenarioProbability || 3}
              onChange={(e) => handleChange('scenarioProbability', parseInt(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value={1}>Très Faible (&lt; 20%)</option>
              <option value={2}>Faible (20-50%)</option>
              <option value={3}>Élevée (50-80%)</option>
              <option value={4}>Très Élevée (&gt; 80%)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Difficulté technique
            </label>
            <select
              value={data.scenarioDifficulty || 2}
              onChange={(e) => handleChange('scenarioDifficulty', parseInt(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value={1}>Négligeable</option>
              <option value={2}>Faible</option>
              <option value={3}>Modérée</option>
              <option value={4}>Élevée</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

// Standard Method Form Component
const StandardMethodForm = ({ data, onChange }) => {
  const phases = ['CONNAITRE', 'RENTRER', 'TROUVER', 'EXPLOITER'];

  const handleStepProbabilityChange = (stepIndex, value) => {
    const newProbabilities = [...(data.stepProbabilities || [3, 3, 3, 3])];
    newProbabilities[stepIndex] = value;
    onChange({
      ...data,
      stepProbabilities: newProbabilities
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Niveau de défense organisationnel
        </label>
        <select
          value={data.defenseLevel || 'intermediate'}
          onChange={(e) => onChange({ ...data, defenseLevel: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="low">Faible</option>
          <option value="intermediate">Intermédiaire</option>
          <option value="high">Élevé</option>
          <option value="very_high">Très Élevé</option>
        </select>
      </div>

      <div>
        <h5 className="font-medium text-gray-900 mb-2 text-sm">
          Probabilité par phase EBIOS RM
        </h5>
        <div className="space-y-2">
          {phases.map((phase, index) => (
            <div key={phase} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
              <span className="text-sm font-medium text-gray-700">{phase}</span>
              <select
                value={data.stepProbabilities?.[index] || 3}
                onChange={(e) => handleStepProbabilityChange(index, parseInt(e.target.value))}
                className="p-1 border border-gray-300 rounded text-sm"
              >
                <option value={1}>Très Faible</option>
                <option value={2}>Faible</option>
                <option value={3}>Significative</option>
                <option value={4}>Très Élevée</option>
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Advanced Method Form Component
const AdvancedMethodForm = ({ data, onChange }) => {
  const phases = ['CONNAITRE', 'RENTRER', 'TROUVER', 'EXPLOITER'];

  const handleStepChange = (stepIndex, field, value) => {
    const currentData = { ...data };
    if (!currentData[field]) {
      currentData[field] = [3, 3, 3, 3];
    }
    const newValues = [...currentData[field]];
    newValues[stepIndex] = value;
    onChange({
      ...currentData,
      [field]: newValues
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Niveau de défense organisationnel
        </label>
        <select
          value={data.defenseLevel || 'intermediate'}
          onChange={(e) => onChange({ ...data, defenseLevel: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="low">Faible</option>
          <option value="intermediate">Intermédiaire</option>
          <option value="high">Élevé</option>
          <option value="very_high">Très Élevé</option>
        </select>
      </div>

      <div>
        <h5 className="font-medium text-gray-900 mb-2 text-sm">
          Double cotation par phase EBIOS RM
        </h5>
        <div className="space-y-3">
          {phases.map((phase, index) => {
            const probability = data.stepProbabilities?.[index] || 3;
            const difficulty = data.stepDifficulties?.[index] || 2;
            const vraisemblance = calculateVraisemblance(probability, difficulty);

            return (
              <div key={phase} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-700 text-sm">{phase}</span>
                  <div className={`px-2 py-1 rounded text-xs font-medium ${vraisemblance === 4 ? 'bg-red-100 text-red-800' :
                    vraisemblance === 3 ? 'bg-orange-100 text-orange-800' :
                      vraisemblance === 2 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                    }`}>
                    Vraisemblance: {vraisemblance}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Probabilité
                    </label>
                    <select
                      value={probability}
                      onChange={(e) => handleStepChange(index, 'stepProbabilities', parseInt(e.target.value))}
                      className="w-full p-1 border border-gray-300 rounded text-sm"
                    >
                      <option value={1}>Très Faible</option>
                      <option value={2}>Faible</option>
                      <option value={3}>Significative</option>
                      <option value={4}>Très Élevée</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Difficulté
                    </label>
                    <select
                      value={difficulty}
                      onChange={(e) => handleStepChange(index, 'stepDifficulties', parseInt(e.target.value))}
                      className="w-full p-1 border border-gray-300 rounded text-sm"
                    >
                      <option value={1}>Négligeable</option>
                      <option value={2}>Faible</option>
                      <option value={3}>Modérée</option>
                      <option value={4}>Élevée</option>
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Method Info Modal Component - follows GuideModal pattern
const MethodInfoModal = ({ onClose }) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="bg-purple-100 p-2 rounded-lg mr-3">
              <Info className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">{t('workshop4.risk.helpMethods', 'Calculation Methods')}</h2>
              <p className="text-sm text-gray-500">{t('workshop4.risk.helpSubtitle', 'EBIOS RM vraisemblance calculation methods')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="space-y-4">
            {/* Express Method */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <span className="text-xl mr-3">⚡</span>
                <h4 className="font-semibold text-green-800">{t('workshop4.risk.methods.express.label')}</h4>
                <span className="ml-auto text-xs bg-green-200 text-green-700 px-2 py-1 rounded-full">2-5 min</span>
              </div>
              <p className="text-sm text-green-700 mb-2">
                {t('workshop4.risk.express.desc', 'Direct V or calculated via MATRIX[DT][PS]')}
              </p>
              <div className="text-xs text-green-600">✓ {t('common.fast', 'Fast')} ✓ {t('common.simple', 'Simple')} ✓ {t('common.firstEvaluation', 'First evaluation')}</div>
            </div>

            {/* Standard Method */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <span className="text-xl mr-3">📊</span>
                <h4 className="font-semibold text-blue-800">{t('workshop4.risk.methods.standard.label')}</h4>
                <span className="ml-auto text-xs bg-blue-200 text-blue-700 px-2 py-1 rounded-full">5-15 min</span>
              </div>
              <p className="text-sm text-blue-700 mb-2">
                {t('workshop4.risk.standard.desc', 'V(Mode) = MIN(PS of actions), V(Scenario) = MAX(V modes)')}
              </p>
              <p className="text-xs text-blue-500 mb-2">
                {t('workshop4.risk.standard.note', 'PS = Probability of Success (1=low, 4=high)')}
              </p>
              <div className="text-xs text-blue-600">✓ {t('common.balanced', 'Balanced')} ✓ {t('common.byPhase', 'By phase')} ✓ {t('common.modesSupport', 'Multi-mode')}</div>
            </div>

            {/* Advanced Method with Matrix */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <span className="text-xl mr-3">🎯</span>
                <h4 className="font-semibold text-purple-800">{t('workshop4.risk.methods.advanced.label')}</h4>
                <span className="ml-auto text-xs bg-purple-200 text-purple-700 px-2 py-1 rounded-full">15-30 min</span>
              </div>
              <p className="text-sm text-purple-700 mb-4">
                {t('workshop4.risk.advanced.desc', 'V(Mode) = MATRIX[MAX(DT)][MIN(PS)] for each mode')}
              </p>

              {/* Vraisemblance Matrix */}
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="text-sm font-medium text-purple-600 mb-3 text-center">{t('workshop4.risk.matrix.title', 'Vraisemblance Matrix')}</div>
                <div className="grid grid-cols-5 gap-1.5 text-sm text-center max-w-sm mx-auto">
                  {/* Header */}
                  <div className="bg-slate-300 p-2 font-bold rounded">DT\PS</div>
                  <div className="bg-blue-200 p-2 font-bold text-blue-800 rounded">1</div>
                  <div className="bg-blue-200 p-2 font-bold text-blue-800 rounded">2</div>
                  <div className="bg-blue-200 p-2 font-bold text-blue-800 rounded">3</div>
                  <div className="bg-blue-200 p-2 font-bold text-blue-800 rounded">4</div>
                  {/* Row 1: DT=1 */}
                  <div className="bg-green-200 p-2 font-bold text-green-800 rounded">1</div>
                  <div className="bg-emerald-300 p-2 font-bold rounded">1</div>
                  <div className="bg-yellow-200 p-2 font-bold rounded">2</div>
                  <div className="bg-orange-300 p-2 font-bold rounded">3</div>
                  <div className="bg-red-400 p-2 font-bold text-white rounded">4</div>
                  {/* Row 2: DT=2 */}
                  <div className="bg-green-200 p-2 font-bold text-green-800 rounded">2</div>
                  <div className="bg-emerald-300 p-2 font-bold rounded">1</div>
                  <div className="bg-yellow-200 p-2 font-bold rounded">2</div>
                  <div className="bg-orange-300 p-2 font-bold rounded">3</div>
                  <div className="bg-orange-300 p-2 font-bold rounded">3</div>
                  {/* Row 3: DT=3 */}
                  <div className="bg-green-200 p-2 font-bold text-green-800 rounded">3</div>
                  <div className="bg-emerald-300 p-2 font-bold rounded">1</div>
                  <div className="bg-emerald-300 p-2 font-bold rounded">1</div>
                  <div className="bg-yellow-200 p-2 font-bold rounded">2</div>
                  <div className="bg-orange-300 p-2 font-bold rounded">3</div>
                  {/* Row 4: DT=4 */}
                  <div className="bg-green-200 p-2 font-bold text-green-800 rounded">4</div>
                  <div className="bg-emerald-300 p-2 font-bold rounded">1</div>
                  <div className="bg-emerald-300 p-2 font-bold rounded">1</div>
                  <div className="bg-yellow-200 p-2 font-bold rounded">2</div>
                  <div className="bg-yellow-200 p-2 font-bold rounded">2</div>
                </div>
                <div className="flex justify-center gap-6 mt-3 text-xs text-slate-500">
                  <span><strong>PS</strong> = {t('workshop4.risk.matrix.ps', 'Probability of Success')}</span>
                  <span><strong>DT</strong> = {t('workshop4.risk.matrix.dt', 'Technical Difficulty')}</span>
                </div>
              </div>

              <div className="text-xs text-purple-600 mt-3">✓ {t('common.precise', 'Precise')} ✓ ANSSI Matrix ✓ {t('common.realTime', 'Real-time')}</div>
            </div>

            {/* Legend */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-sm font-medium text-gray-700 mb-2">{t('common.legend', 'Legend')}:</div>
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="px-3 py-1.5 bg-emerald-300 rounded-full font-medium">V=1 {t('common.low', 'Low')}</span>
                <span className="px-3 py-1.5 bg-yellow-200 rounded-full font-medium">V=2 {t('common.medium', 'Medium')}</span>
                <span className="px-3 py-1.5 bg-orange-300 rounded-full font-medium">V=3 {t('common.high', 'High')}</span>
                <span className="px-3 py-1.5 bg-red-400 text-white rounded-full font-medium">V=4 {t('common.critical', 'Critical')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500">
            <strong>Note:</strong> {t('workshop4.risk.automaticNote', 'Calculations are automatic. Just modify the parameters, results update instantly.')}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            {t('common.close', 'Close')}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ScenarioRiskCalculation;
